import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection, SubjectStatus, TalkStatus } from 'constants/index'
import { caseInsensitiveSort, getUniqueArrayOfStrings, getVenueTitle } from 'utils'
import { signOut } from 'store/auth'
import { chunk, sortBy } from 'lodash'
import { updateSubjectStatus } from 'store/subject'
import { toast } from 'react-toastify'
import { fetchAssociatedPolls, getMultipleDocsByIdFromCollection } from 'store/polling'
import { getUpdatedTalksAlongEventAndSession } from 'utils/talk'
import moment from 'moment'
import { sortDataArrayByCreatedAt } from 'utils/times'

export const initialState = {
  error: null,
  inProgress: false,
  talks: [],
  talk: null,
  subjectTalksMap: new Map(),
  lastAbstractNumber: 0,
  lastPosterNumber: 0,
  selectedDate: '',
  selectedSpeaker: null,
  selectedVenue: 'All',
  talkSpeakers: [],
  talkVenues: []
}

const talkSlice = createSlice({
  name: 'talk',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setTalks: (state, action) => {
      state.talks = action.payload
    },
    setTalk: (state, action) => {
      state.talk = action.payload
    },
    setSubjectTalksMap: (state, action) => {
      state.subjectTalksMap = new Map(action.payload)
    },
    setLastAbstractNumber: (state, action) => {
      state.lastAbstractNumber = action.payload
    },
    setLastPosterNumber: (state, action) => {
      state.lastPosterNumber = action.payload
    },
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload
    },
    setSelectedSpeaker: (state, action) => {
      state.selectedSpeaker = action.payload
    },
    setSelectedVenue: (state, action) => {
      state.selectedVenue = action.payload
    },
    setTalkSpeakers: (state, action) => {
      state.talkSpeakers = action.payload
    },
    setTalkVenues: (state, action) => {
      state.talkVenues = action.payload
    },
    filterTalks: (state, action) => {
      const talkId = action.payload
      state.talks = state.talks.filter((talk) => talk.id !== talkId)
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.talks = []
      state.talk = null
      state.subjectTalksMap = new Map()
      state.lastAbstractNumber = 0
      state.lastPosterNumber = 0
      state.selectedDate = ''
      state.selectedSpeaker = null
      state.selectedVenue = 'All'
      state.talkSpeakers = []
      state.talkVenues = []
    }
  }
})

export const addTalk = (payload) => async (dispatch) => {
  try {
    if (payload.abstractNumber) {
      dispatch(talkSlice.actions.setLastAbstractNumber(payload.abstractNumber))
    } else if (payload.posterNumber) {
      dispatch(talkSlice.actions.setLastPosterNumber(payload.posterNumber))
    }
    const doc = await FirestoreDatabase.collection(Collection.TALKS).add({
      ...payload,
      createdAt: moment().utc().valueOf()
    })
    return doc.id
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  }
}

export const checkNumberExistsInTalks =
  (fieldName, num, editedTalkId) => async (dispatch, getState) => {
    try {
      dispatch(talkSlice.actions.setProgress(true))
      const user = getState().auth.user

      const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
        .where('conferenceId', '==', user?.conferenceId)
        .where(fieldName, '==', num)
        .get()

      const talks = talksSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
      if (editedTalkId) {
        const filtered = talks.filter((talk) => talk.id !== editedTalkId)
        return !!filtered?.length
      }

      return !!talks?.length
    } catch (err) {
      dispatch(talkSlice.actions.setError(err))
    } finally {
      dispatch(talkSlice.actions.setProgress(false))
    }
  }

export const getLastNumber = (fieldName) => async (dispatch, getState) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .orderBy(fieldName, 'desc')
      .limit(1)
      .get()
    const docData = snapshot.docs[0].data()
    const lastNumber = docData && docData[fieldName] ? docData[fieldName] : 0
    if (fieldName === 'abstractNumber') {
      dispatch(talkSlice.actions.setLastAbstractNumber(lastNumber))
    } else {
      dispatch(talkSlice.actions.setLastPosterNumber(lastNumber))
    }
    return lastNumber
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  } finally {
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchUnAssociatedTalks = (conferenceId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))

    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', conferenceId)
      .where('status', '==', TalkStatus.NOT_ASSOCIATED)
      .get()
    const talks = talksSnapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
    dispatch(talkSlice.actions.setTalks(talks))
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchConferenceTalks = (conferenceId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))

    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const talks = talksSnapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
    let filteredTalks = talks?.filter(
      (talk) => talk?.status === undefined || talk?.status === TalkStatus.ASSOCIATED
    )
    const talkSpeakerIds = []
    filteredTalks?.forEach((talk) => {
      if (talk?.speakers?.length) {
        talkSpeakerIds.push(...talk.speakers)
      }
    })

    const venueIds = getUniqueArrayOfStrings(filteredTalks, 'venueId')
    const uniqueSpeakerIds = talkSpeakerIds.length ? Array.from(new Set(talkSpeakerIds)) : []

    const promises = [
      getMultipleDocsByIdFromCollection(venueIds, Collection.VENUES),
      getMultipleDocsByIdFromCollection(uniqueSpeakerIds, Collection.SPEAKERS)
    ]
    const [venues, speakers] = await Promise.all(promises)
    const formattedVenues = venues?.map((venue) => ({
      ...venue,
      venueText: getVenueTitle(venue?.title, venue?.floorNum, venue?.roomNum)
    }))
    dispatch(talkSlice.actions.setTalkSpeakers(speakers))
    dispatch(talkSlice.actions.setTalkVenues(formattedVenues))

    filteredTalks = sortDataArrayByCreatedAt(filteredTalks).map((item, i) => ({
      ...item,
      srNo: i + 1
    }))
    dispatch(talkSlice.actions.setTalks(filteredTalks))
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchEventOrSessionTalks = async (eventId, sessionId) => {
  try {
    const snapshot = sessionId
      ? await FirestoreDatabase.collection(Collection.TALKS)
          .where('sessionId', '==', sessionId)
          .get()
      : await FirestoreDatabase.collection(Collection.TALKS).where('eventId', '==', eventId).get()

    return snapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
  } catch (err) {
    console.error(err)
  }
}

export const fetchConferenceTalksAlongData = (subjectId, conferenceId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const docSnap = await FirestoreDatabase.collection(Collection.CONFERENCES)
      .doc(conferenceId)
      .get()
    const conference = docSnap.exists ? { id: docSnap.id, ...docSnap?.data() } : null
    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('subjectId', '==', subjectId)
      .get()
    const talks = talksSnapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
    const eventIds = getUniqueArrayOfStrings(talks, 'eventId')
    const sessionIds = getUniqueArrayOfStrings(talks, 'sessionId')
    const promises = [
      getMultipleDocsByIdFromCollection(eventIds, Collection.EVENTS),
      getMultipleDocsByIdFromCollection(sessionIds, Collection.SESSIONS)
    ]
    const [events, sessions] = await Promise.all(promises)
    const updatedTalks = getUpdatedTalksAlongEventAndSession(
      talks,
      events,
      sessions,
      conference?.timezone,
      conference?.is24hrFormat
    )
    const notAssociated = updatedTalks.filter((talk) => talk.status === TalkStatus.NOT_ASSOCIATED)
    const associated = updatedTalks.filter((talk) => talk.status === TalkStatus.ASSOCIATED)
    const notAssociatedOrdered = sortBy(notAssociated, (obj) => obj?.title?.toLowerCase())
    const associatedOrdered = sortBy(associated, (obj) => obj?.eventStartTime)

    const talksUpdated = [...notAssociatedOrdered, ...associatedOrdered]
    dispatch(talkSlice.actions.setTalks(talksUpdated))
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchSubjectsTalks = (subjectId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('subjectId', '==', subjectId)
      .get()

    const talks = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
    const orderedTalks = sortBy(talks, (obj) => obj?.title?.toLowerCase())
    dispatch(talkSlice.actions.setTalks(orderedTalks))
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchMultipleSubjectTalks = (subjectIds) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const talks = []
    const batchSize = 10
    const bachedIds = chunk(subjectIds, batchSize)

    const subjectTalksMap = new Map()

    for (const batchIds of bachedIds) {
      const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
        .where('subjectId', 'in', batchIds)
        .get()
      snapshot.docs.forEach((doc) => {
        talks.push({ id: doc?.id, ...doc.data() })
      })
    }
    talks.forEach((talk) => {
      const { subjectId } = talk
      if (subjectTalksMap.has(subjectId)) {
        subjectTalksMap.set(subjectId, subjectTalksMap.get(subjectId) + 1)
      } else {
        subjectTalksMap.set(subjectId, 1)
      }
    })
    dispatch(talkSlice.actions.setSubjectTalksMap(subjectTalksMap))
    dispatch(talkSlice.actions.setProgress(false))
    return subjectTalksMap
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchTalks = (eventId, sessionId, callback) => {
  const query =
    eventId && sessionId
      ? FirestoreDatabase.collection(Collection.TALKS)
          .orderBy('startDate')
          .where('eventId', '==', eventId)
          .where('sessionId', '==', sessionId)
      : FirestoreDatabase.collection(Collection.TALKS)
          .orderBy('startDate')
          .where('eventId', '==', eventId)

  return query.onSnapshot((documents) => {
    const talks = []
    documents.forEach((doc) => {
      talks.push({
        id: doc.id,
        ...doc.data()
      })
    })
    callback(sortDataArrayByCreatedAt(talks).map((item, i) => ({ ...item, srNo: i + 1 })))
  })
}

export const fetchEventTalksAlongPolls = (eventId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const talksData = await FirestoreDatabase.collection(Collection.TALKS)
      .orderBy('startDate')
      .where('eventId', '==', eventId)
      .where('havePolls', '==', true)
      .get()

    const talks = talksData?.docs?.map((doc) => ({ id: doc.id, ...doc.data(), type: 'Talk Poll' }))

    const pollIdsList = talks?.map((talk) => talk?.pollId)
    const polls = await fetchAssociatedPolls(pollIdsList)

    const updatedTalks = talks.map((talk) => {
      const poll = polls?.find((e) => e.id === talk?.pollId)
      return {
        ...talk,
        pollId: poll?.id,
        pollTitle: poll?.title,
        pollPresenter: poll?.presenter,
        pollStatus: poll?.status
      }
    })

    dispatch(talkSlice.actions.setTalks(caseInsensitiveSort(updatedTalks, 'title')))
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchSessionTalksAlongPolls = (sessionId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const sessionPromise = FirestoreDatabase.collection(Collection.SESSIONS).doc(sessionId).get()
    const talksPromise = FirestoreDatabase.collection(Collection.TALKS)
      .orderBy('startDate')
      .where('sessionId', '==', sessionId)
      .where('havePolls', '==', true)
      .get()

    const promises = [sessionPromise, talksPromise]
    const [sessionDoc, talksData] = await Promise.all(promises)

    const session = { id: sessionDoc.id, ...sessionDoc.data() }
    const talks = talksData?.docs?.map((doc) => ({ id: doc.id, ...doc.data(), type: 'Talk Poll' }))

    const pollIdsList = talks?.map((talk) => talk?.pollId)
    const polls = await fetchAssociatedPolls(
      session?.havePolls ? [session?.pollId, ...pollIdsList] : pollIdsList
    )

    const updatedData = session?.havePolls
      ? [{ ...session, type: 'Session Poll' }, ...talks]
      : talks

    updatedData.forEach((data) => {
      const poll = polls?.find((e) => e.id === data.pollId)
      data.pollId = poll?.id
      data.pollTitle = poll?.title
      data.pollPresenter = poll?.presenter
      data.pollStatus = poll?.status
    })

    dispatch(talkSlice.actions.setTalks(caseInsensitiveSort(updatedData, 'title')))
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const fetchTalkById = (talkId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).get()
    const e = snapshot.data()
    dispatch(
      talkSlice.actions.setTalk({
        id: snapshot.id,
        ...e
      })
    )
    dispatch(talkSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const updateTalkById = (talkId, payload) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).update(payload)
    return talkId
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  }
}

export const updateTalksWithSpeaker = async (subjectId, speakerId) => {
  const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
    .where('subjectId', '==', subjectId)
    .get()
  const talks = talksSnap.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
  const batch = FirestoreDatabase.batch()
  talks.forEach((talk) => {
    const docRef = FirestoreDatabase.collection(Collection.TALKS).doc(talk.id)
    const talkSpeakers = talk?.speakers ?? []
    if (!talkSpeakers.includes(speakerId)) {
      const speakers = talkSpeakers?.length ? [...talkSpeakers, speakerId] : [speakerId]
      batch.update(docRef, { speakers })
    }
  })
  await batch.commit()
}

export const deleteTalkById = (talkId) => async (dispatch) => {
  try {
    dispatch(talkSlice.actions.setProgress(true))
    const docSnap = await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).get()
    const talk = { id: docSnap?.id, ...docSnap.data() }
    if (talk?.havePolls && talk?.pollId) {
      await FirestoreDatabase.collection(Collection.POLLS).doc(talk?.pollId).update({
        alreadyUsed: false,
        eventId: null,
        sessionId: null,
        talkId: null
      })
    }
    await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).delete()
    dispatch(talkSlice.actions.filterTalks(talkId))
    toast.success('Talk deleted successfully.')
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  } finally {
    dispatch(talkSlice.actions.setProgress(false))
  }
}

export const deleteTalkAlongPoll =
  (talkId, { forApproval }) =>
  async (dispatch) => {
    try {
      const docSnap = await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).get()
      const talk = { id: docSnap?.id, ...docSnap.data() }
      if (talk?.havePolls && talk?.pollId) {
        const questionSnap = await FirestoreDatabase.collection(Collection.POLLS)
          .doc(talk?.pollId)
          .collection(Collection.QUESTIONS)
          .get()

        const batch = FirestoreDatabase.batch()
        questionSnap?.docs?.forEach((question) => {
          const docRef = FirestoreDatabase.collection(Collection.POLLS)
            .doc(talk?.pollId)
            .collection(Collection.QUESTIONS)
            .doc(question.id)
          batch.delete(docRef)
        })
        batch.delete(FirestoreDatabase.collection(Collection.POLLS).doc(talk?.pollId))
        batch.delete(FirestoreDatabase.collection(Collection.TALKS).doc(talkId))
        await batch.commit()
      } else {
        await FirestoreDatabase.collection(Collection.TALKS).doc(talkId).delete()
      }

      if (!forApproval) {
        const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
          .where('subjectId', '==', talk?.subjectId)
          .get()
        const talks = talksSnap?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
        const notAssociatedTalks = talks?.filter(
          (talk) => talk?.status === TalkStatus.NOT_ASSOCIATED
        )
        if (talks?.length && talks?.length !== notAssociatedTalks?.length) {
          await dispatch(updateSubjectStatus(talk?.subjectId, SubjectStatus.APPROVED))
        }
      }
      dispatch(talkSlice.actions.filterTalks(talkId))
    } catch (err) {
      dispatch(talkSlice.actions.setError(err))
    }
  }

export const resetTalkStore = () => (dispatch) => {
  try {
    dispatch(talkSlice.actions.setTalk(null))
    dispatch(talkSlice.actions.setTalks([]))
  } catch (err) {
    dispatch(talkSlice.actions.setError(err))
  }
}

export default talkSlice
